import { REASONING_LEVELS } from '../schemas/base-schemas';
import { getBedrockCapabilities } from './model-capabilities';
/**
 * 推論レベルの既定値（チャット画面の初期値、段階が渡されない呼び出しの既定）
 */
export const DEFAULT_REASONING_LEVEL = 'medium';
/**
 * 推論レベルの画面上の名称（UI と AI の設定読み取りで共有する）
 */
export const REASONING_LEVEL_LABELS = {
    off: 'オフ',
    low: '低',
    medium: '中',
    high: '高',
    xhigh: '超高',
    max: '最大',
};
/**
 * 旧形式の推論モードを推論レベルへ変換する。
 *
 * ON はいずれも high。OFF はチャット画面の保存値だけ medium とする
 * （旧形式の OFF でも Claude Opus 5.5 / Sonnet 5.5 は実際にはモデル既定の深さで推論していたため）。
 * アドバイザー設定の off は利用者が明示的に選んだ値、要求の off は判定用の内部呼び出しが
 * 推論なしを前提に送る値なので、off のまま扱う。
 */
export function fromLegacyReasoningMode(mode, context) {
    if (mode === 'on') {
        return 'high';
    }
    return context === 'chat-preference' ? 'medium' : 'off';
}
/**
 * 旧形式のフィールドへ書き戻す互換値（旧バージョンのアプリ・Lambda 向け）。off 以外はすべて on
 */
export function toLegacyReasoningMode(level) {
    return level === 'off' ? 'off' : 'on';
}
/**
 * モデルが受け付ける段階の集合へ、選ばれた段階を丸める。
 * 受け付ける段階はそのまま、受け付けない段階は順序上もっとも近い段階にする（距離が同じなら浅い側）。
 *
 * @param supported - 受け付ける段階（空でない）
 */
export function roundReasoningLevel(supported, level) {
    const target = REASONING_LEVELS.indexOf(level);
    let best = supported[0];
    let bestDistance = Number.POSITIVE_INFINITY;
    // 浅い順に走査し、距離が真に小さいときだけ更新するので、同距離なら浅い側が残る
    for (const candidate of supported) {
        const distance = Math.abs(REASONING_LEVELS.indexOf(candidate) - target);
        if (distance < bestDistance) {
            best = candidate;
            bestDistance = distance;
        }
    }
    return best;
}
/**
 * モデルのエフォートの選び方の形を返す。
 *
 * @param modelId - resolveModelId で解決済みの具体的モデルID
 */
export function getReasoningMenuShape(modelId) {
    const { reasoning } = getBedrockCapabilities(modelId);
    switch (reasoning.kind) {
        case 'adaptive':
        case 'effort':
            return 'levels';
        case 'none':
            return 'unsupported';
    }
}
/**
 * 選ばれた段階を、モデルが受け付ける実効レベルへ丸める。
 * UI の表示とチャット Lambda の送信内容は、どちらもこの関数の結果を使う。
 *
 * @param modelId - resolveModelId で解決済みの具体的モデルID
 * @param level - 利用者が選んだ段階
 */
export function resolveReasoningLevel(modelId, level) {
    const { reasoning } = getBedrockCapabilities(modelId);
    switch (reasoning.kind) {
        case 'adaptive':
        case 'effort': {
            const effectiveLevel = roundReasoningLevel(reasoning.levels, level);
            return { effectiveLevel, display: effectiveLevel };
        }
        case 'none':
            return { effectiveLevel: null, display: null };
    }
}
/**
 * 6 段階それぞれについて、モデルで選べるかを浅い順に返す（エフォートのサブメニュー用）。
 * エフォートを指定できないモデル（unsupported）はすべて選べないとする。
 *
 * @param modelId - resolveModelId で解決済みの具体的モデルID
 */
export function listReasoningLevelOptions(modelId) {
    const { reasoning } = getBedrockCapabilities(modelId);
    const supported = reasoning.kind === 'adaptive' || reasoning.kind === 'effort' ? reasoning.levels : [];
    return REASONING_LEVELS.map((level) => ({ level, selectable: supported.includes(level) }));
}
//# sourceMappingURL=reasoning-levels.js.map